import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ChevronRight } from "lucide-react";
import { FOUNDATIONS } from "../config/foundations";

// Ordered to match the `home.themes` array in the locale files.
export const EXPLORE_SLUGS = [
  "artificial-intelligence",
  "longevity",
  "health",
  "philosophy",
  "beauty",
  "intentional-living",
  "leadership-character",
  "future-of-humanity",
];

// Each theme leads onward to a related Foundation chapter — never a dead loop.
const THEME_TO_FOUNDATION = [
  "technology-human-potential",
  "health-lifestyle-medicine",
  "health-lifestyle-medicine",
  "wisdom-lifelong-learning",
  "beauty-culture-environment",
  "human-flourishing",
  "character-purpose",
  "technology-human-potential",
];

// A quiet, editorial destination for each "What We Explore" theme.
export default function ExploreTopic() {
  const { topic } = useParams();
  const { t } = useTranslation();
  useEffect(() => { window.scrollTo(0, 0); }, [topic]);

  const themes = t("home.themes", { returnObjects: true }) || [];
  const foundations = t("home.foundations", { returnObjects: true }) || [];
  const idx = EXPLORE_SLUGS.indexOf(topic);
  const theme = idx >= 0 ? themes[idx] : null;

  if (!theme) return <Navigate to="/" replace />;

  const foundationSlug = THEME_TO_FOUNDATION[idx];
  const foundationIdx = FOUNDATIONS.findIndex((f) => f.slug === foundationSlug);
  const foundationNode = foundationIdx >= 0 ? foundations[foundationIdx] : null;

  return (
    <div className="pb-24" data-testid={`explore-topic-${topic}`}>
      {/* Breadcrumb */}
      <nav className="max-w-7xl mx-auto px-6 md:px-12 pt-6" aria-label="Breadcrumb" data-testid="explore-breadcrumb">
        <ol className="flex items-center gap-2 text-[10px] font-sans tracking-[0.2em] uppercase text-secondary-custom">
          <li><Link to="/" className="hover:text-[var(--accent-bronze)] transition-colors">{t("nav.home")}</Link></li>
          <ChevronRight className="w-3 h-3 opacity-50" />
          <li className="text-primary-custom">{theme.title}</li>
        </ol>
      </nav>

      <div className="min-h-[60vh] flex items-center justify-center py-20">
        <div className="max-w-2xl mx-auto px-6 text-center space-y-8">
          <span className="text-[10px] font-sans tracking-[0.35em] uppercase text-[var(--accent-bronze)] font-semibold block">
            {t("home.exploreEyebrow")}
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-light text-primary-custom tracking-tight leading-[1.08]">
            {theme.title}
          </h1>
          <p className="font-serif text-base sm:text-lg italic text-secondary-custom leading-relaxed font-light max-w-xl mx-auto">
            {theme.desc}
          </p>
          <div className="w-px h-12 bg-subtle-custom mx-auto" />
          <p className="font-sans text-xs sm:text-sm text-secondary-custom leading-relaxed max-w-md mx-auto">
            {t("home.exploreNote")}
          </p>

          <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
            {foundationNode && (
              <Link
                to={`/foundations/${foundationSlug}`}
                data-testid="explore-related-foundation"
                className="inline-flex items-center gap-2 border border-primary bg-primary-custom text-primary-custom px-8 py-3 text-[10px] uppercase tracking-[0.25em] font-medium hover:bg-[var(--accent-bronze)] hover:text-white hover:border-[var(--accent-bronze)] transition-all duration-300"
              >
                <span>{foundationNode.title}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
            <Link
              to="/journal"
              data-testid="explore-browse-journal"
              className="inline-flex items-center gap-2 border border-subtle-custom bg-surface-custom px-8 py-3 text-[10px] uppercase tracking-[0.25em] font-medium hover:border-[var(--accent-bronze)] transition-all duration-300"
            >
              <span>{t("home.readJournal")}</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
